import {
  Avatar,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Grid,
  Icon,
} from "@mui/material";
import { ArrowRight, ArrowDropDown } from "@mui/icons-material";
import { getCompetitionsUsersList } from "apis/compete";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import moment from "moment";
import UserKpiRow from "./UserKpiRow";
import RemainingTime from "./RemainingTime";
import InfoPopover from "./InfoPopover";

const prefix = process.env.REACT_APP_CDN_ASSET_PREFIX || "/assets";
const Trophy = `${prefix}/images/badges/trophy.png`;

function Row({
  competition,
  selectedCompetition,
  status,
  handleClick,
  calculateDuration,
  setIsDisplayKpi,
  setCompetitionInfo,
  selectedUser: filteredUser,
  handleCompetitionJoin,
}) {
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [payload, setPayload] = useState({
    pageNumber: 0,
    pageSize: 20,
    competitionId: selectedCompetition,
    searchBy: "",
  });
  const [selectedUser, setSelectedUser] = useState();

  const getUsers = async () => {
    try {
      setIsLoading(true);
      const response = await getCompetitionsUsersList(payload);
      if (response?.data?.success) {
        setUsers(response?.data?.data);
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (payload.competitionId && selectedCompetition && competition.id === selectedCompetition) {
      getUsers();
    }
  }, [selectedCompetition, payload?.competitionId, competition?.competitionList]);

  useEffect(() => {
    setSelectedUser();
    if (competition.id === selectedCompetition) {
      setPayload((prev) => ({ ...prev, competitionId: selectedCompetition }));
    }
  }, [selectedCompetition, competition]);

  const handleSearchFilter = (user) => {
    if (!payload.searchBy) {
      return true;
    }
    return user?.User?.name?.toLowerCase()?.includes(payload?.searchBy?.toLowerCase());
  };

  const findMyRank = () => (typeof competition.myRank !== "undefined" ? competition.myRank : "-");

  return (
    <TableCell sx={{ p: 0 }} colSpan={8}>
      <Collapse
        in={competition.id === selectedCompetition}
        timeout="auto"
        unmountOnExit
        sx={{
          boxShadow:
            "inset 0rem 2rem 0.5rem -2rem rgba(0, 0, 0, 0.1), inset 0rem -2rem 0.5rem -2rem rgba(0, 0, 0, 0.1)",
        }}
      >
        <MDBox sx={{ marginY: 1 }}>
          <Grid container p={2} direction="column">
            <Grid item>
              <MDTypography
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: "400",
                  wordBreak: "break-word",
                }}
              >
                {competition.description}
              </MDTypography>
            </Grid>
            {isMobileDisplay && (
              <>
                <Grid
                  item
                  className="trophy-head-cell"
                  sx={{ pt: 2, display: "flex", justifyContent: "space-between" }}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    <img src={Trophy} alt="trophy" />
                    <MDTypography className="listing-table-header">
                      {status === "Open" ? "Current Leader" : "Winner"}
                    </MDTypography>
                  </Stack>
                  {competition?.currentLeader?.length > 0 &&
                  (status === "Open" ||
                    (status === "Closed" && competition.showCompetitionRank)) ? (
                    <Stack spacing={1} direction="row" alignItems="center">
                      <Avatar
                        variant="circular"
                        src={competition?.currentLeader?.[0]?.profilePicture}
                        alt="profile-picture"
                        sx={{ imageRendering: "-webkit-optimize-contrast" }}
                      />
                      <MDTypography>{competition?.currentLeader?.[0]?.name || "-"}</MDTypography>
                      {competition?.currentLeader?.length > 1 && (
                        <MDBox
                          sx={{ display: "flex", ml: 1 }}
                          onClick={(event) => handleClick(event, competition.currentLeader)}
                        >
                          <MoreHorizIcon />
                        </MDBox>
                      )}
                    </Stack>
                  ) : (
                    "-"
                  )}
                </Grid>
                <Grid item className="listing-table-mobile-grid">
                  <MDTypography className="listing-table-header">Participants</MDTypography>
                  <Box>{competition?.competitionList?.length || 0}</Box>
                </Grid>
                {status === "Upcoming" && (
                  <Grid item className="listing-table-mobile-grid">
                    <MDTypography className="listing-table-header">Duration</MDTypography>
                    <Box>
                      {competition.endDatetime
                        ? calculateDuration(competition.startDatetime, competition.endDatetime)
                        : "-"}
                    </Box>
                  </Grid>
                )}
                {status !== "Upcoming" && (
                  <Grid item className="listing-table-mobile-grid">
                    <MDTypography className="listing-table-header">
                      {filteredUser && filteredUser?.id !== loginUser.id
                        ? `${filteredUser?.name}'s`
                        : "My"}{" "}
                      Rank
                    </MDTypography>
                    <Box>
                      {competition.type === "tremendous payout" &&
                      (status === "Open" ||
                        (status === "Closed" && competition.showCompetitionRank)) &&
                      competition.competitionList?.find(
                        (userInCompetition) => userInCompetition.userId === loginUser.id
                      )
                        ? findMyRank()
                        : "-"}
                    </Box>
                  </Grid>
                )}
                {status === "Open" && (
                  <Grid item className="listing-table-mobile-grid">
                    <MDTypography className="listing-table-header">Time Remaining</MDTypography>
                    <Box>
                      {competition?.endDatetime ? (
                        <RemainingTime endDate={competition.endDatetime} />
                      ) : (
                        "-"
                      )}
                    </Box>
                  </Grid>
                )}
                {status === "Upcoming" && (
                  <Grid item className="listing-table-mobile-grid">
                    <MDTypography className="listing-table-header">Start Time</MDTypography>
                    <Box>
                      {moment
                        .tz(competition.startDatetime, loginUser.timezone)
                        .format("dddd MMMM D, hh:mm a")}
                    </Box>
                  </Grid>
                )}
                {status !== "Open" && (
                  <Grid item className="listing-table-mobile-grid">
                    <MDTypography className="listing-table-header">End Time</MDTypography>
                    <Box>
                      {competition?.endDatetime
                        ? moment
                            .tz(competition.endDatetime, loginUser.timezone)
                            .format("dddd MMMM D, hh:mm a")
                        : "-"}
                    </Box>
                  </Grid>
                )}
                <Grid item display="flex" pt={2} justifyContent="space-between">
                  <Stack alignItems="center">
                    <Icon>
                      {competition.type === "tremendous payout" ? "security" : "remove_moderator"}
                    </Icon>
                    <Typography className="icon-typography">
                      {competition.type === "tremendous payout" ? "Tremendous" : "Unranked"}
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" onClick={() => handleCompetitionJoin(competition)}>
                    <Icon>{competition.joinable ? "lock_open" : "lock"}</Icon>
                    <Typography className="icon-typography">
                      {competition.joinable ? "Joinable" : "Locked"}
                    </Typography>
                  </Stack>
                  <Stack alignItems="center">
                    <Icon
                      onClick={() => {
                        setCompetitionInfo(competition);
                        setIsDisplayKpi(true);
                      }}
                    >
                      view_headline
                    </Icon>
                    <Typography className="icon-typography">View KPIs</Typography>
                  </Stack>
                  <InfoPopover competition={competition} />
                </Grid>
              </>
            )}
          </Grid>
          <MDBox sx={{ display: "flex" }}>
            <TableContainer
              sx={{
                width: "100%",
                position: "relative",
                borderRadius: 0,
                boxShadow: "none",
              }}
            >
              <Loader loading={isLoading} />
              <Table>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {competition.type === "tremendous payout" &&
                      (status === "Open" ||
                        (status === "Closed" && competition?.showCompetitionRank)) && <TableCell />}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography className="listing-table-header">User</Typography>
                        <MDInput
                          sx={{ width: "100%", maxWidth: "8rem", ml: "1rem" }}
                          placeholder="Search User"
                          label="Search User"
                          value={payload.searchBy}
                          onChange={(event) =>
                            setPayload((prevState) => ({
                              ...prevState,
                              searchBy: event.target.value,
                              pageNumber: 0,
                            }))
                          }
                        />
                      </Box>
                    </TableCell>
                    {status !== "Upcoming" && (
                      <TableCell align="center">
                        <Typography className="listing-table-header">
                          {competition?.CompetitionWinCondition?.condition === "Total Points"
                            ? "Total Points"
                            : "Total KPIs"}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users?.filter(handleSearchFilter).map((user) => (
                    <React.Fragment key={user.id}>
                      <TableRow
                        className="user-list-row"
                        key={user.id}
                        hover
                        onClick={() => {
                          if (user.id === selectedUser?.id) {
                            setSelectedUser();
                          } else {
                            setSelectedUser(user);
                          }
                        }}
                      >
                        {competition.type === "tremendous payout" &&
                          (status === "Open" ||
                            (status === "Closed" && competition?.showCompetitionRank)) && (
                            <TableCell align="center" width="12%" className="user-list-cell">
                              <MDTypography
                                sx={{
                                  fontSize: "18px !important",
                                  fontWeight: "600 !important",
                                }}
                              >
                                {user?.rank}
                              </MDTypography>
                            </TableCell>
                          )}
                        <TableCell className="user-list-cell">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar
                              variant="circular"
                              sx={{
                                "& .MuiAvatar-img": {
                                  height: "100%",
                                  objectFit: "initial",
                                },
                              }}
                              src={user?.User?.profilePicture}
                              alt="profile-picture"
                            />
                            <MDTypography>{user?.User?.name}</MDTypography>
                            {user.id === selectedUser?.id ? (
                              <ArrowDropDown
                                style={{ cursor: "pointer" }}
                                sx={{
                                  ml: "-3px",
                                }}
                              />
                            ) : (
                              <ArrowRight
                                style={{ cursor: "pointer" }}
                                sx={{
                                  ml: "-3px",
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        {status !== "Upcoming" && (
                          <TableCell align="center" className="user-list-cell">
                            {competition?.CompetitionWinCondition?.condition === "Total Points"
                              ? user?.totalPoints
                              : user?.totalCounts}
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow className="user-list-kpi-row">
                        <UserKpiRow
                          competition={competition}
                          user={user}
                          status={status}
                          selectedUser={selectedUser}
                        />
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </MDBox>
      </Collapse>
    </TableCell>
  );
}

export default Row;
